/* eslint-disable react/prop-types */
import { FC, useEffect, useRef, useState } from "react";
import {
	FormProvider,
	SubmitHandler,
	useForm,
	useFormState
} from "react-hook-form";
import { ArrowLeft, Upload } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "src/components/ui/button";
import {
	FormField,
	FormItem,
	FormLabel,
	FormControl
} from "src/components/ui/form";
import { Input } from "src/components/ui/input";
import { Checkbox } from "src/components/ui/checkbox";
import isEmpty from "lodash.isempty";
import { KeyShoutFormValues } from "../../adapters/formValuesToKeyShout";
import { safeImageUrl } from "src/utils/safeImageUrl";

export type { KeyShoutFormValues };

interface Props {
	defaultValues: KeyShoutFormValues;
	onSubmit: SubmitHandler<KeyShoutFormValues>;
	isSubmitting?: boolean;
	isEdit?: boolean;
	onDelete?: () => void;
	existingImageUrl?: string | null;
}

const ALLOWED_IMAGE_TYPES = ["image/jpeg", "image/jpg", "image/png"];
const MAX_IMAGE_SIZE = 2_097_152; // 2 MB

const KeyShoutForm: FC<Props> = ({
	defaultValues,
	onSubmit,
	isSubmitting = false,
	isEdit = false,
	onDelete,
	existingImageUrl
}) => {
	const methods = useForm<KeyShoutFormValues>({
		mode: "onChange",
		reValidateMode: "onChange",
		defaultValues
	});

	const { reset, control, handleSubmit, watch, setValue } = methods;
	const { isDirty, errors } = useFormState({ control });

	const fileInputRef = useRef<HTMLInputElement>(null);
	const [previewUrl, setPreviewUrl] = useState<string | null>(null);
	const [imageRemoved, setImageRemoved] = useState(false);
	const [imageError, setImageError] = useState<string | null>(null);
	const [isDragging, setIsDragging] = useState(false);
	const [submitAttempted, setSubmitAttempted] = useState(false);

	const imageFile = watch("image");

	const safeExistingUrl = safeImageUrl(existingImageUrl);
	const displayUrl = imageRemoved ? null : previewUrl || safeExistingUrl;
	const hasImage = !!imageFile || (!!safeExistingUrl && !imageRemoved);
	const showImageRequiredError = !hasImage && (submitAttempted || imageRemoved);

	const isSubmitBtnDisabled =
		(!isDirty && !imageRemoved) ||
		!isEmpty(errors) ||
		isSubmitting ||
		!!imageError;

	useEffect(() => {
		reset(defaultValues);
		setImageRemoved(false);
		setImageError(null);
		setSubmitAttempted(false);
	}, [defaultValues]);

	useEffect(() => {
		if (imageFile) {
			const url = URL.createObjectURL(imageFile);
			setPreviewUrl(url);
			return () => URL.revokeObjectURL(url);
		} else {
			setPreviewUrl(null);
		}
	}, [imageFile]);

	const validateAndSetFile = (file: File) => {
		if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
			setImageError("Only JPG and PNG files are allowed.");
			if (fileInputRef.current) fileInputRef.current.value = "";
			return;
		}
		if (file.size > MAX_IMAGE_SIZE) {
			setImageError("Image must be under 2 MB.");
			if (fileInputRef.current) fileInputRef.current.value = "";
			return;
		}
		setImageError(null);
		setValue("image", file, { shouldDirty: true });
		setImageRemoved(false);
	};

	const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (file) validateAndSetFile(file);
	};

	const handleDragOver = (e: React.DragEvent) => {
		e.preventDefault();
		setIsDragging(true);
	};

	const handleDragLeave = (e: React.DragEvent) => {
		e.preventDefault();
		setIsDragging(false);
	};

	const handleDrop = (e: React.DragEvent) => {
		e.preventDefault();
		setIsDragging(false);
		const file = e.dataTransfer.files[0];
		if (file) validateAndSetFile(file);
	};

	const handleRemoveImage = () => {
		setValue("image", null, { shouldDirty: true });
		setPreviewUrl(null);
		setImageRemoved(true);
		setImageError(null);
		if (fileInputRef.current) {
			fileInputRef.current.value = "";
		}
	};

	return (
		<form
			onSubmit={(e) => {
				setSubmitAttempted(true);
				handleSubmit((data) => {
					if (!hasImage) return;
					onSubmit(data);
				})(e);
			}}
			onKeyDown={(e) => {
				if (e.key === "Enter" && e.target instanceof HTMLInputElement) {
					e.preventDefault();
				}
			}}
		>
			<FormProvider {...methods}>
				<div className="flex flex-col gap-4">
					{/* Heading */}
					<div
						className={[
							"rounded-xl border bg-subtle shadow-sm transition-colors",
							errors.heading ? "border-red-400/60" : "border-border"
						].join(" ")}
					>
						{errors.heading && (
							<div className="relative">
								<span
									role="alert"
									className="absolute -top-2.5 right-3 bg-red-500 text-white text-xs px-2 py-0.5 rounded-full font-medium"
								>
									Please fill out the required field
								</span>
							</div>
						)}
						<div className="p-6">
							<label className="font-heading text-lg font-medium text-foreground mb-4 block">
								Heading text
							</label>
							<FormField
								control={control}
								name="heading"
								rules={{
									required: "Heading is required",
									maxLength: {
										value: 40,
										message: "Maximum 40 characters"
									}
								}}
								render={({ field }) => (
									<FormItem className="space-y-0">
										<FormControl>
											<Input
												placeholder="Maximum heading count is 40 characters."
												maxLength={40}
												className="w-full bg-white"
												{...field}
											/>
										</FormControl>
									</FormItem>
								)}
							/>
							<p className="text-[13px] text-zinc-500 text-right mt-1">
								{watch("heading")?.length || 0} / 40
							</p>
						</div>
					</div>

					{/* Content */}
					<div className="rounded-xl border border-border bg-subtle shadow-sm">
						<div className="p-6">
							<label className="font-heading text-lg font-medium text-foreground mb-4 block">
								Content text
							</label>
							<FormField
								control={control}
								name="content"
								rules={{
									maxLength: {
										value: 80,
										message: "Maximum 80 characters"
									}
								}}
								render={({ field }) => (
									<FormItem className="space-y-0">
										<FormControl>
											<Input
												placeholder="Maximum content count is 80 characters."
												maxLength={80}
												className="w-full bg-white"
												{...field}
											/>
										</FormControl>
									</FormItem>
								)}
							/>
							<p className="text-[13px] text-zinc-500 text-right mt-1">
								{watch("content")?.length || 0} / 80
							</p>
						</div>
					</div>

					{/* Image */}
					<div
						className={[
							"rounded-xl border bg-subtle shadow-sm transition-colors",
							showImageRequiredError ? "border-red-400/60" : "border-border"
						].join(" ")}
					>
						{showImageRequiredError && (
							<div className="relative">
								<span
									role="alert"
									className="absolute -top-2.5 right-3 bg-red-500 text-white text-xs px-2 py-0.5 rounded-full font-medium"
								>
									An image is required
								</span>
							</div>
						)}
						<div className="p-6">
							<label className="font-heading text-lg font-medium text-foreground mb-4 block">
								Add image
							</label>
							<p className="text-[13px] text-muted-foreground mb-4">
								jpg or png, 250 x 250px
							</p>
							<input
								ref={fileInputRef}
								type="file"
								accept="image/jpeg,image/jpg,image/png"
								className="hidden"
								onChange={handleFileChange}
							/>
							<div className="max-w-[480px]">
								{displayUrl ? (
									<>
										<div className="mb-3 rounded-lg overflow-hidden border border-border bg-subtle max-h-[400px]">
											<img
												src={displayUrl}
												alt="Uploaded image preview"
												className="object-contain w-full h-auto max-h-[400px]"
											/>
										</div>
										<div className="flex items-center gap-2">
											<button
												type="button"
												className="inline-flex items-center justify-center px-5 h-9 rounded-lg border border-foreground bg-white text-foreground text-sm font-medium cursor-pointer hover:bg-foreground hover:text-white transition-colors"
												onClick={() => fileInputRef.current?.click()}
											>
												Change image
											</button>
											<button
												type="button"
												className="inline-flex items-center justify-center px-5 h-9 rounded-lg border border-red-300 bg-white text-red-600 text-sm font-medium cursor-pointer hover:bg-red-600 hover:text-white hover:border-red-600 transition-colors"
												onClick={handleRemoveImage}
											>
												Remove
											</button>
										</div>
									</>
								) : (
									<div
										role="button"
										tabIndex={0}
										onClick={() => fileInputRef.current?.click()}
										onKeyDown={(e) => {
											if (e.key === "Enter" || e.key === " ") {
												e.preventDefault();
												fileInputRef.current?.click();
											}
										}}
										onDragOver={handleDragOver}
										onDragLeave={handleDragLeave}
										onDrop={handleDrop}
										className={[
											"flex flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed px-6 py-10 text-center transition-colors cursor-pointer",
											isDragging
												? "border-primary bg-primary-lighter"
												: "border-border bg-white hover:border-primary-light hover:bg-primary-lighter/50"
										].join(" ")}
									>
										<Upload className="h-6 w-6 text-muted-foreground" />
										<div>
											<p className="text-sm font-medium text-foreground">
												Drag image here or click to browse
											</p>
											<p className="text-xs text-muted-foreground mt-1">
												JPG or PNG
											</p>
										</div>
									</div>
								)}
							</div>
							{imageError && (
								<p className="text-sm text-red-500 mt-2">{imageError}</p>
							)}
						</div>
					</div>

					{/* Link (optional) */}
					<div className="rounded-xl border border-border bg-subtle shadow-sm">
						<div className="p-6">
							<label className="font-heading text-lg font-medium text-foreground mb-4 block">
								Link (optional)
							</label>

							<div className="flex flex-col gap-5">
								<FormField
									control={control}
									name="buttonLink"
									rules={{
										validate: (v: string) => {
											if (!v) return true;
											if (/^(javascript|data|vbscript):/i.test(v)) {
												return "This URL type is not allowed";
											}
											return true;
										}
									}}
									render={({ field }) => (
										<FormItem>
											<FormLabel className="text-sm font-medium text-zinc-600">
												URL
											</FormLabel>
											<FormControl>
												<Input
													placeholder="https://example.com"
													className="w-full bg-white"
													{...field}
												/>
											</FormControl>
										</FormItem>
									)}
								/>

								<FormField
									control={control}
									name="webview"
									render={({ field }) => (
										<FormItem className="flex items-center gap-2">
											<FormControl>
												<Checkbox
													checked={field.value}
													onCheckedChange={field.onChange}
												/>
											</FormControl>
											<FormLabel className="!mt-0 text-sm text-zinc-700 cursor-pointer">
												Open in app
											</FormLabel>
										</FormItem>
									)}
								/>
							</div>
						</div>
					</div>
				</div>

				<div className="flex items-center justify-between pt-8 mt-8 border-t border-border">
					<div className="flex items-center gap-4">
						<Link
							to="/app/homepage/key-shouts"
							className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-foreground transition-colors"
						>
							<ArrowLeft className="h-4 w-4" />
							Back to key shouts
						</Link>

						{isEdit && onDelete && (
							<Button type="button" variant="destructive" onClick={onDelete}>
								Delete
							</Button>
						)}
					</div>

					<Button type="submit" disabled={isSubmitBtnDisabled}>
						{isSubmitting ? (
							<div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
						) : (
							"Save"
						)}
					</Button>
				</div>
			</FormProvider>
		</form>
	);
};

export default KeyShoutForm;
